import React, { Suspense, Fragment, useState, useEffect } from "react";
import { Layout, Spin, Row, Upload, message, Button, Form, Col, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../helpers/appDispatch";
import { appSelector } from "../../helpers/appSelector";
import { clearStates, uploadFile, downloadSampleFileRequest } from "../../store/bulk-payment";

const { Content } = Layout;

interface Props {
  onBack: () => void;
}

const FileUpload: React.FC<Props> = ({ onBack }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [excel, setExcel] = useState("");
  const [excelFile, setExcelFile] = useState<File>();
  const { 
    uploadingFile, 
    success, 
    failure, 
    uploadSuccess, 
    downloadingSampleFile, 
    downloadSampleFileError,
    error 
  } = appSelector(
    (state) => state.bulkPayment
  );

  const { user } = appSelector((state) => state.auth);

  // Clear error message after a timeout or on unmount
  useEffect(() => {
    if (failure || error) {
      const timer = setTimeout(() => {
        dispatch(clearStates());
      }, 10000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [failure, error, dispatch]);

  // Show success message when upload is successful
  useEffect(() => {
    if (uploadSuccess) {
      message.success(t('success.fileUpload') || 'File uploaded successfully');
      dispatch(clearStates());
    }
  }, [uploadSuccess, t, dispatch]);

  const beforeUpload = (file: File): boolean => {
    const isExcel =
      file.type === "text/csv" ||
      file.name.slice(file.name.lastIndexOf(".")) === ".xlsx";

    if (!isExcel) {
      message.error(`${t("general.fileUploadTypeError")}`);
    }
    setExcel(file.name);
    setExcelFile(file);
    return isExcel;
  };
  const uploadButton: React.ReactNode = (
    <div>
      {<PlusOutlined />}
      <div className="ant-upload-text">{t("general.chooseFile")}</div>
    </div>
  );

  let container: React.ReactNode;
  if (uploadingFile) {
    container = (
      <Row className="suspense-container">
        <Spin style={{ marginTop: "20px" }} />
      </Row>
    );
  }

  const onValidateFile = () => {
    const formData = new FormData();
    if (excelFile) {
      formData.append("File", excelFile, excelFile.name);
      formData.append("fileName", excelFile.name);

      dispatch(uploadFile(formData));
    }
  };

  const handleDownloadSample = () => {
    dispatch(downloadSampleFileRequest());
  };

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        {downloadSampleFileError && (
          <Alert
            message={t('general.fileDownloadError') || 'Failed to download sample file'}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
          />
        )}

        {/* Display upload error message as Alert */}
        {(failure || error) && (
          <Alert
            message={t("general.fileUploadError") || "Error uploading file"}
            description={typeof error === 'object' ? error?.message : error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => dispatch(clearStates())}
          />
        )}

        {/* Display success message as Alert */}
        {uploadSuccess && (
          <Alert
            message={t('success.fileUpload') || 'File uploaded successfully'}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => dispatch(clearStates())}
          />
        )}

        <Suspense
          fallback={
            <Row
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <div style={{ marginTop: "200px" }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <div className="margin-top">
              <Row style={{ position: "relative", justifyContent: "space-between", width: "100%" }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={onBack}
                  style={{ marginBottom: "20px" }}
                >
                  {t("general.back")}
                </Button>
                <Button
                  type="primary"
                  onClick={handleDownloadSample}
                  loading={downloadingSampleFile}
                  style={{ marginBottom: "20px" }}
                >
                  {t("general.downloadSample")}
                </Button>
              </Row>
            </div>

            <div className="flexy justify-content-end mt-10">
              <Col span={6}>
                {/* <Form.Item name="channel">
                  <Select
                    placeholder={`${t("general.selectProvider")}`}
                    style={{ width: "100%" }}
                    onChange={() => handleChangeEvent()}
                    optionFilterProp="children"
                    filterOption={(input: any, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    allowClear
                    showSearch
                  >
                    {providers.length &&
                      providers.map((p) => (
                        <Option value={p.name} key={p.name}>
                          {p.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item> */}
              </Col>
            </div>

            {!success ? (
              <div>
                <div className="upload border">
                  <Upload
                    name="Logo"
                    listType="picture-card"
                    className="seo-uploader"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                  >
                    {!uploadingFile
                      ? excel.length
                        ? excel
                        : uploadButton
                      : container}
                  </Upload>
                </div>
                <Form.Item className="btn-input login-row-second">
                  <Button
                    type="primary"
                    className="resetButton"
                    onClick={() => onValidateFile()}
                    disabled={!excel || uploadingFile}
                  >
                    {t("general.uploadFile")}
                  </Button>
                </Form.Item>
              </div>
            ) : null}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  );
};

export default FileUpload;
